import { getSubscriberPageForEnrollment } from 'api/operations/electionService';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { bpHistory } from 'global/bpHistory';
import { FiltersObject } from 'store/general/paginationDelegate';
import IAddEmployeesPage from 'store/modules/add-employees/iAddEmployeesPage';
import {
  EmployeeInvitationStatusTypes,
  SubscriberAddEmployeesProgress,
  SubscribersFilter,
} from 'types/electionService';
import { enrollmentEmployeeActions } from 'store/pages/enrollment/add-employees/enrollmentEmployeeActions';
import { getStandardActionsWithSelfEnrollFeature } from 'store/modules/add-employees/inviteEmployeeActions';

interface ManageAddEmployeesDomainParams {
  periodId: string;
  groupId: string;
}

export interface ManageAddEmployeesFilters extends FiltersObject {
  status: EmployeeInvitationStatusTypes | null;
  subProgress: SubscriberAddEmployeesProgress | null;
  employeeClass: string | null;
  employeeName: string;
}

export default class ManageAddEmployeesPageStore extends IAddEmployeesPage<ManageAddEmployeesFilters> {
  protected _domainParams: ManageAddEmployeesDomainParams;

  public get stepCanComplete(): boolean {
    return this.employees.length > 0;
  }

  public async getEmployees(): Promise<void> {
    this._setIsLoading(true);
    const { result, totalElements, totalPages } = await getSubscriberPageForEnrollment({
      groupId: this._domainParams.groupId,
      periodId: this._domainParams.periodId,
      sortBy: this.paginationDelegate.sortBy,
      sortOrder: this.paginationDelegate.sortOrder,
      status: this.paginationDelegate.filters.status ? [this.paginationDelegate.filters.status] : [],
      employeeClasses: this.paginationDelegate.filters.employeeClass
        ? [this.paginationDelegate.filters.employeeClass]
        : [],
      progress: this.paginationDelegate.filters.subProgress ? [this.paginationDelegate.filters.subProgress] : [],
      filter: SubscribersFilter.ALL,
      name: this.paginationDelegate.filters.employeeName,
      page: this.paginationDelegate.page,
    });
    runInAction(() => {
      this._createEmployeeDelegates(result);
      this.paginationDelegate.setTotalElements(totalElements);
      this.paginationDelegate.setTotalPages(totalPages);
      this._setIsLoading(false);
    });
  }

  public onBack(): void {
    bpHistory.push(`/employee-elections/${this._domainParams.periodId}?groupId=${this._domainParams.groupId}`);
  }

  constructor(params: ManageAddEmployeesDomainParams) {
    super(
      { status: null, employeeName: '', subProgress: null, employeeClass: null },
      getStandardActionsWithSelfEnrollFeature(enrollmentEmployeeActions),
      params,
    );
    this._domainParams = params;

    makeObservable<ManageAddEmployeesPageStore, '_domainParams'>(this, {
      _domainParams: observable,
      stepCanComplete: computed,
      getEmployees: action.bound,
      onBack: action.bound,
    });
  }
}

const ManageAddEmployeesPageStoreContext = createContext(
  new ManageAddEmployeesPageStore({} as ManageAddEmployeesDomainParams),
);

export const useManageAddEmployeesPageStore = (): ManageAddEmployeesPageStore => {
  return useContext(ManageAddEmployeesPageStoreContext);
};

export { ManageAddEmployeesPageStore, ManageAddEmployeesPageStoreContext };
